import React, { useState, useMemo } from 'react';
import {
  Award,
  Plus,
  PlusCircle,
  Calendar,
  X,
  Filter,
  Download,
  Edit2,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  Gift,
  Layers,
  Clock,
  Sparkles,
  Users,
  Tag,
  ArrowRight,
  ShieldCheck,
  FileSpreadsheet,
} from 'lucide-react';
import { BonusEventItem, PointsLedgerTransaction, AdminStudent, AdminRole } from '../types';
import { Badge, Button, Input, Search, Tooltip } from '../../components/core';

export const EVENT_TYPES = [
  'Paryushan',
  'Mahavir Jayanti',
  'Special Pathshala Event',
  'Sunday Class Bonus',
  'Competition Participation',
  'Attendance Campaign',
  'Custom Events',
] as const;

export const PATHSHALA_LEVELS = [
  'Level 1 - Prarambhik',
  'Level 2 - Madhyamik',
  'Level 3 - Shravak Junior',
  'All Levels',
];

interface BonusPointsProps {
  bonusEvents?: BonusEventItem[];
  events?: BonusEventItem[];
  transactions: PointsLedgerTransaction[];
  students: AdminStudent[];
  currentRole: AdminRole;
  onAddBonusEvent?: (evt: BonusEventItem) => void;
  onAddEvent?: (evt: BonusEventItem) => void;
  onUpdateBonusEvent?: (evt: BonusEventItem) => void;
  onUpdateEvent?: (evt: BonusEventItem) => void;
  onDeleteBonusEvent?: (id: string) => void;
  onDeleteEvent?: (id: string) => void;
  onAdjustPoints: (
    studentId: string,
    points: number,
    reason: string,
    type: 'Credit' | 'Debit',
    actorName: string
  ) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const BonusPoints: React.FC<BonusPointsProps> = ({
  bonusEvents,
  events,
  transactions,
  students,
  currentRole,
  onAddBonusEvent,
  onAddEvent,
  onUpdateBonusEvent,
  onUpdateEvent,
  onDeleteBonusEvent,
  onDeleteEvent,
  onAdjustPoints,
  addToast,
}) => {
  const isAuditor = currentRole === 'Auditor';

  // Consolidate events list
  const activeEventsList = useMemo(() => {
    return bonusEvents || events || [];
  }, [bonusEvents, events]);

  // Tab State: 'events' | 'ledger'
  const [activeTab, setActiveTab] = useState<'events' | 'ledger'>('events');

  // Search & Filter state for Ledger
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerEventFilter, setLedgerEventFilter] = useState('ALL');
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState('ALL');

  // Search state for Events
  const [eventSearch, setEventSearch] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('ALL');
  const [eventStatusFilter, setEventStatusFilter] = useState('ALL');

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<BonusEventItem | null>(null);
  const [viewingEvent, setViewingEvent] = useState<BonusEventItem | null>(null);

  // Award Points Modal
  const [isAwardModalOpen, setIsAwardModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(students[0]?.id || '');
  const [awardEventTitle, setAwardEventTitle] = useState('');
  const [awardPoints, setAwardPoints] = useState(50);
  const [awardReason, setAwardReason] = useState('Bonus Points Awarded');
  const [awardType, setAwardType] = useState<'Credit' | 'Debit'>('Credit');

  // Event Form State
  const [eventForm, setEventForm] = useState({
    title: '',
    eventType: 'Paryushan',
    description: '',
    pointsValue: 50,
    eligibleLevels: ['Level 1 - Prarambhik', 'Level 2 - Madhyamik', 'Level 3 - Shravak Junior'],
    applicableStudents: 'All Enrolled Students',
    startDate: '2026-08-01',
    endDate: '2026-08-10',
    status: 'Active' as 'Active' | 'Inactive',
  });

  // Event Handlers
  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const newEvt: BonusEventItem = {
      id: `EVT-${Date.now().toString().slice(-4)}`,
      title: eventForm.title,
      eventType: eventForm.eventType,
      description: eventForm.description,
      pointsValue: Number(eventForm.pointsValue) || 0,
      eligibleLevels: eventForm.eligibleLevels.length > 0 ? eventForm.eligibleLevels : ['All Levels'],
      applicableStudents: eventForm.applicableStudents || 'All Students',
      startDate: eventForm.startDate,
      endDate: eventForm.endDate,
      eventDate: eventForm.endDate,
      status: eventForm.status,
    };

    if (onAddEvent) onAddEvent(newEvt);
    else if (onAddBonusEvent) onAddBonusEvent(newEvt);

    addToast('success', 'Event Created', `Bonus Event "${newEvt.title}" created successfully.`);
    setIsCreateModalOpen(false);
    resetEventForm();
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent || isAuditor) return;

    const updatedEvt: BonusEventItem = {
      ...editingEvent,
      title: eventForm.title,
      eventType: eventForm.eventType,
      description: eventForm.description,
      pointsValue: Number(eventForm.pointsValue) || 0,
      eligibleLevels: eventForm.eligibleLevels.length > 0 ? eventForm.eligibleLevels : ['All Levels'],
      applicableStudents: eventForm.applicableStudents || 'All Students',
      startDate: eventForm.startDate,
      endDate: eventForm.endDate,
      eventDate: eventForm.endDate,
      status: eventForm.status,
    };

    if (onUpdateEvent) onUpdateEvent(updatedEvt);
    else if (onUpdateBonusEvent) onUpdateBonusEvent(updatedEvt);

    addToast('success', 'Event Updated', `Updated details for "${updatedEvt.title}".`);
    setEditingEvent(null);
    resetEventForm();
  };

  const handleToggleStatus = (evt: BonusEventItem) => {
    if (isAuditor) return;
    const nextStatus = evt.status === 'Active' ? 'Inactive' : 'Active';
    const updatedEvt: BonusEventItem = { ...evt, status: nextStatus };

    if (onUpdateEvent) onUpdateEvent(updatedEvt);
    else if (onUpdateBonusEvent) onUpdateBonusEvent(updatedEvt);

    addToast('info', 'Status Changed', `Event "${evt.title}" is now ${nextStatus}.`);
  };

  const handleDelete = (evt: BonusEventItem) => {
    if (isAuditor) return;
    if (onDeleteEvent) onDeleteEvent(evt.id);
    else if (onDeleteBonusEvent) onDeleteBonusEvent(evt.id);

    addToast('warning', 'Event Removed', `Deleted bonus event "${evt.title}".`);
  };

  const openEditModal = (evt: BonusEventItem) => {
    setEditingEvent(evt);
    setEventForm({
      title: evt.title,
      eventType: evt.eventType || 'Paryushan',
      description: evt.description,
      pointsValue: evt.pointsValue,
      eligibleLevels: evt.eligibleLevels || ['All Levels'],
      applicableStudents: evt.applicableStudents || 'All Enrolled Students',
      startDate: evt.startDate || evt.eventDate || '2026-08-01',
      endDate: evt.endDate || evt.eventDate || '2026-08-10',
      status: evt.status || 'Active',
    });
  };

  const resetEventForm = () => {
    setEventForm({
      title: '',
      eventType: 'Paryushan',
      description: '',
      pointsValue: 50,
      eligibleLevels: ['Level 1 - Prarambhik', 'Level 2 - Madhyamik', 'Level 3 - Shravak Junior'],
      applicableStudents: 'All Enrolled Students',
      startDate: '2026-08-01',
      endDate: '2026-08-10',
      status: 'Active',
    });
  };

  // Award Points Submit
  const handleAwardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const matchedStu = students.find((s) => s.id === selectedStudentId);
    if (!matchedStu) return;

    const pointsNum = awardType === 'Credit' ? Math.abs(awardPoints) : -Math.abs(awardPoints);
    const fullReason = awardEventTitle ? `[${awardEventTitle}] ${awardReason}` : awardReason;

    onAdjustPoints(selectedStudentId, pointsNum, fullReason, awardType, 'Admin Office');
    addToast(
      'success',
      'Points Awarded',
      `Awarded ${pointsNum > 0 ? '+' : ''}${pointsNum} pts to ${matchedStu.firstName} ${matchedStu.surname}`
    );
    setIsAwardModalOpen(false);
  };

  // Level Checkbox Toggle
  const handleLevelToggle = (lvl: string) => {
    if (eventForm.eligibleLevels.includes(lvl)) {
      setEventForm({
        ...eventForm,
        eligibleLevels: eventForm.eligibleLevels.filter((l) => l !== lvl),
      });
    } else {
      setEventForm({
        ...eventForm,
        eligibleLevels: [...eventForm.eligibleLevels, lvl],
      });
    }
  };

  // Export CSV for Ledger
  const handleExportCSV = () => {
    if (transactions.length === 0) {
      addToast('info', 'No Data', 'No ledger transactions available to export.');
      return;
    }

    const headers = ['Transaction ID', 'Awarded Date', 'Student Name', 'Reason / Event', 'Type', 'Points Awarded', 'Awarded By'];
    const rows = filteredTransactions.map((tx) => [
      `"${tx.id}"`,
      `"${tx.timestamp}"`,
      `"${tx.studentName}"`,
      `"${tx.reason.replace(/"/g, '""')}"`,
      `"${tx.type}"`,
      `"${tx.points > 0 ? '+' : ''}${tx.points}"`,
      `"${tx.adjustedBy}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `points_ledger_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addToast('success', 'Ledger Exported', 'Downloaded points ledger CSV report.');
  };

  // Filtered Events
  const filteredEvents = useMemo(() => {
    return activeEventsList.filter((evt) => {
      const matchesSearch =
        evt.title.toLowerCase().includes(eventSearch.toLowerCase()) ||
        evt.description.toLowerCase().includes(eventSearch.toLowerCase()) ||
        (evt.eventType && evt.eventType.toLowerCase().includes(eventSearch.toLowerCase()));

      const matchesType = eventTypeFilter === 'ALL' || evt.eventType === eventTypeFilter;
      const matchesStatus = eventStatusFilter === 'ALL' || (evt.status || 'Active') === eventStatusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [activeEventsList, eventSearch, eventTypeFilter, eventStatusFilter]);

  // Filtered Transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      const matchesSearch =
        tx.studentName.toLowerCase().includes(ledgerSearch.toLowerCase()) ||
        tx.reason.toLowerCase().includes(ledgerSearch.toLowerCase()) ||
        tx.adjustedBy.toLowerCase().includes(ledgerSearch.toLowerCase());

      const matchesEvent = ledgerEventFilter === 'ALL' || tx.reason.includes(ledgerEventFilter);
      const matchesType = ledgerTypeFilter === 'ALL' || tx.type === ledgerTypeFilter;

      return matchesSearch && matchesEvent && matchesType;
    });
  }, [transactions, ledgerSearch, ledgerEventFilter, ledgerTypeFilter]);

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Bonus Events & Points Ledger</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!isAuditor && (
            <>
              <Button
                size="sm"
                leftIcon={PlusCircle}
                className="bg-amber-800 hover:bg-amber-900"
                onClick={() => {
                  setSelectedStudentId(students[0]?.id || '');
                  setAwardEventTitle('');
                  setIsAwardModalOpen(true);
                }}
              >
                Award Points
              </Button>
              <Button
                size="sm"
                leftIcon={Plus}
                onClick={() => {
                  resetEventForm();
                  setIsCreateModalOpen(true);
                }}
              >
                Create Bonus Event
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Sub-Navigation Bar */}
      <div className="admin-card flex items-center gap-2 p-2">
        <button
          type="button"
          onClick={() => setActiveTab('events')}
          className={`flex cursor-pointer items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition-all ${
            activeTab === 'events' ? 'admin-tab-active' : 'admin-tab-idle'
          }`}
        >
          <Gift className="h-4 w-4" />
          <span>Bonus Events Catalog ({activeEventsList.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('ledger')}
          className={`flex cursor-pointer items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition-all ${
            activeTab === 'ledger' ? 'admin-tab-active' : 'admin-tab-idle'
          }`}
        >
          <Award className="h-4 w-4" />
          <span>Points Audit Ledger ({transactions.length})</span>
        </button>
      </div>

      {/* TAB 1: BONUS EVENTS CATALOG */}
      {activeTab === 'events' && (
        <div className="space-y-4">
          {/* Controls Bar */}
          <div className="admin-card flex flex-col items-center justify-between gap-3 p-4 lg:flex-row">
            <Search
              value={eventSearch}
              onChange={setEventSearch}
              placeholder="Search events by title or description..."
              containerClassName="w-full lg:w-72 lg:flex-1"
            />

            <div className="flex w-full flex-wrap items-center gap-2.5 lg:w-auto">
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                className="admin-input rounded-xl border border-[var(--gv-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
              >
                <option value="ALL">All Event Types</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

              <select
                value={eventStatusFilter}
                onChange={(e) => setEventStatusFilter(e.target.value)}
                className="admin-input rounded-xl border border-[var(--gv-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
              >
                <option value="ALL">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>

              {(eventSearch || eventTypeFilter !== 'ALL' || eventStatusFilter !== 'ALL') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEventSearch('');
                    setEventTypeFilter('ALL');
                    setEventStatusFilter('ALL');
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Events Grid */}
          {filteredEvents.length === 0 ? (
            <div className="admin-card space-y-2 p-12 text-center">
              <Gift className="mx-auto h-8 w-8 text-[var(--gv-ink-muted)]" />
              <p className="text-sm font-bold text-[var(--gv-ink)]">No Bonus Events Found</p>
              <p className="text-xs text-[var(--gv-ink-muted)]">Create a new bonus event or adjust your search filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredEvents.map((evt) => {
                const isActive = (evt.status || 'Active') === 'Active';

                return (
                  <div
                    key={evt.id}
                    className="admin-card flex flex-col justify-between space-y-4 p-5 transition-all hover:border-stone-300"
                  >
                    <div className="space-y-3">
                      {/* Top Badges */}
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge variant="soft" className="normal-case">
                          {evt.eventType || 'Special Event'}
                        </Badge>

                        <div className="flex items-center gap-1.5">
                          <Badge variant={isActive ? 'success' : 'outline'} className="normal-case">
                            {evt.status || 'Active'}
                          </Badge>

                          <Badge variant="warning" className="font-mono font-extrabold normal-case">
                            +{evt.pointsValue} Pts
                          </Badge>
                        </div>
                      </div>

                      {/* Title & Description */}
                      <div>
                        <h3 className="text-base font-bold leading-snug text-[var(--gv-ink)]">{evt.title}</h3>
                        <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-[var(--gv-ink-muted)]">
                          {evt.description}
                        </p>
                      </div>

                      {/* Meta Info */}
                      <div className="space-y-1.5 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3 text-[11px]">
                        <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                          <span className="font-semibold">Applicable Levels:</span>
                          <span className="max-w-[150px] truncate font-bold text-[var(--gv-ink)]">
                            {evt.eligibleLevels ? evt.eligibleLevels.join(', ') : 'All Levels'}
                          </span>
                        </div>

                        {evt.applicableStudents && (
                          <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                            <span className="font-semibold">Students:</span>
                            <span className="font-bold text-[var(--gv-ink)]">{evt.applicableStudents}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-[var(--gv-ink-muted)]">
                          <span className="font-semibold">Event Dates:</span>
                          <span className="font-mono text-[var(--gv-ink)]">
                            {evt.startDate || evt.eventDate || 'N/A'} to {evt.endDate || evt.eventDate || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions Bar */}
                    <div className="flex items-center justify-between gap-1.5 border-t border-stone-100 pt-3">
                      <div className="flex items-center gap-1">
                        <Tooltip content="View Event Details">
                          <Button
                            variant="ghost"
                            size="icon"
                            leftIcon={Eye}
                            aria-label="View Event Details"
                            onClick={() => setViewingEvent(evt)}
                          />
                        </Tooltip>

                        {!isAuditor && (
                          <Tooltip content="Edit Event">
                            <Button
                              variant="ghost"
                              size="icon"
                              leftIcon={Edit2}
                              aria-label="Edit Event"
                              className="text-[var(--gv-brand)]"
                              onClick={() => openEditModal(evt)}
                            />
                          </Tooltip>
                        )}

                        {!isAuditor && (
                          <Tooltip content={isActive ? 'Deactivate Event' : 'Activate Event'}>
                            <Button
                              variant="ghost"
                              size="icon"
                              leftIcon={isActive ? CheckCircle2 : XCircle}
                              aria-label={isActive ? 'Deactivate Event' : 'Activate Event'}
                              className={
                                isActive
                                  ? 'text-emerald-800 hover:bg-emerald-50'
                                  : undefined
                              }
                              onClick={() => handleToggleStatus(evt)}
                            />
                          </Tooltip>
                        )}

                        {!isAuditor && (
                          <Tooltip content="Delete Event">
                            <Button
                              variant="ghost"
                              size="icon"
                              leftIcon={Trash2}
                              aria-label="Delete Event"
                              className="text-rose-700 hover:bg-rose-50"
                              onClick={() => handleDelete(evt)}
                            />
                          </Tooltip>
                        )}
                      </div>

                      {!isAuditor && (
                        <Button
                          size="sm"
                          leftIcon={Gift}
                          className="bg-amber-800 hover:bg-amber-900"
                          onClick={() => {
                            setSelectedStudentId(students[0]?.id || '');
                            setAwardEventTitle(evt.title);
                            setAwardPoints(evt.pointsValue);
                            setAwardReason(`Bonus awarded for ${evt.title}`);
                            setIsAwardModalOpen(true);
                          }}
                        >
                          Award
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: POINTS AUDIT LEDGER */}
      {activeTab === 'ledger' && (
        <div className="admin-card space-y-3 overflow-hidden">
          {/* Filter Header */}
          <div className="flex flex-col items-center justify-between gap-3 border-b border-[var(--gv-border)] bg-[var(--gv-surface)] p-4 lg:flex-row">
            <Search
              value={ledgerSearch}
              onChange={setLedgerSearch}
              placeholder="Search by student, event, or awarder..."
              containerClassName="w-full lg:w-72 lg:flex-1"
            />

            <div className="flex w-full flex-wrap items-center gap-2.5 lg:w-auto">
              <select
                value={ledgerTypeFilter}
                onChange={(e) => setLedgerTypeFilter(e.target.value)}
                className="admin-input rounded-xl border border-[var(--gv-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
              >
                <option value="ALL">All Types</option>
                <option value="Credit">Credit (+ Points)</option>
                <option value="Debit">Debit (- Points)</option>
              </select>

              <Button variant="outline" size="sm" leftIcon={FileSpreadsheet} onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>
          </div>

          {/* Table */}
          {filteredTransactions.length === 0 ? (
            <div className="space-y-2 bg-[var(--gv-surface)] py-16 text-center">
              <Award className="mx-auto h-8 w-8 text-[var(--gv-ink-muted)]" />
              <p className="text-sm font-bold text-[var(--gv-ink)]">No Ledger Transactions Found</p>
              <p className="text-xs text-[var(--gv-ink-muted)]">No matching student transactions recorded in this log.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
                  <tr>
                    <th className="px-4 py-3 font-bold">Txn ID & Date</th>
                    <th className="px-4 py-3 font-bold">Student Name</th>
                    <th className="px-4 py-3 font-bold">Event / Reason</th>
                    <th className="px-4 py-3 text-center font-bold">Type</th>
                    <th className="px-4 py-3 text-center font-bold">Bonus Points</th>
                    <th className="px-4 py-3 font-bold">Awarded By</th>
                    <th className="px-4 py-3 text-right font-bold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 bg-white font-medium text-[var(--gv-ink)]">
                  {filteredTransactions.map((tx) => (
                    <tr key={tx.id} className="transition-colors hover:bg-stone-50/80">
                      <td className="px-4 py-3.5 font-mono">
                        <div className="font-bold text-[var(--gv-brand)]">{tx.id}</div>
                        <span className="text-[10px] text-[var(--gv-ink-muted)]">{tx.timestamp}</span>
                      </td>

                      <td className="px-4 py-3.5 font-bold text-[var(--gv-ink)]">{tx.studentName}</td>

                      <td className="px-4 py-3.5 text-[var(--gv-ink-muted)]">
                        <div className="font-semibold text-[var(--gv-ink)]">{tx.reason}</div>
                        {tx.eventName && (
                          <span className="font-mono text-[10px] text-[var(--gv-ink-muted)]">Event: {tx.eventName}</span>
                        )}
                      </td>

                      <td className="px-4 py-3.5 text-center">
                        <Badge variant={tx.type === 'Credit' ? 'success' : 'danger'} className="normal-case">
                          {tx.type}
                        </Badge>
                      </td>

                      <td className="px-4 py-3.5 text-center font-mono font-bold text-amber-900">
                        {tx.points > 0 ? `+${tx.points}` : tx.points} pts
                      </td>

                      <td className="px-4 py-3.5 font-mono text-[var(--gv-ink-muted)]">{tx.adjustedBy}</td>

                      <td className="px-4 py-3.5 text-right">
                        <Badge variant="success" className="normal-case">
                          {tx.status || 'Awarded'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CREATE / EDIT EVENT MODAL */}
      {(isCreateModalOpen || editingEvent) && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card my-8 w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingEvent ? 'Edit Bonus Event' : 'Create Special Bonus Event'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setEditingEvent(null);
                }}
              />
            </div>

            <form onSubmit={editingEvent ? handleEditSubmit : handleCreateSubmit} className="space-y-3.5 text-xs">
              <Input
                label="Event Name *"
                type="text"
                required
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="e.g., Paryushan Mahaparva Special Bonus"
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Event Type</label>
                  <select
                    value={eventForm.eventType}
                    onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    {EVENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Bonus Points *"
                  type="number"
                  required
                  min={1}
                  className="font-mono font-bold"
                  value={eventForm.pointsValue}
                  onChange={(e) => setEventForm({ ...eventForm, pointsValue: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description</label>
                <textarea
                  rows={2}
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Details of activities required to earn this bonus..."
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-[var(--gv-ink)]">Applicable Pathshala Levels</label>
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  {PATHSHALA_LEVELS.map((lvl) => (
                    <label key={lvl} className="flex cursor-pointer items-center gap-2 font-medium text-[var(--gv-ink)]">
                      <input
                        type="checkbox"
                        checked={eventForm.eligibleLevels.includes(lvl)}
                        onChange={() => handleLevelToggle(lvl)}
                        className="rounded border-[var(--gv-border)] text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                      />
                      <span>{lvl}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Input
                label="Applicable Students (Optional)"
                type="text"
                value={eventForm.applicableStudents}
                onChange={(e) => setEventForm({ ...eventForm, applicableStudents: e.target.value })}
                placeholder="e.g., All Enrolled Students or Senior Batch"
              />

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Start Date"
                  type="date"
                  required
                  value={eventForm.startDate}
                  onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value })}
                />

                <Input
                  label="End Date"
                  type="date"
                  required
                  value={eventForm.endDate}
                  onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                />
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Status</label>
                <select
                  value={eventForm.status}
                  onChange={(e) => setEventForm({ ...eventForm, status: e.target.value as 'Active' | 'Inactive' })}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 border-t border-[var(--gv-border)] pt-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingEvent(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">{editingEvent ? 'Save Changes' : 'Publish Event'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW EVENT DETAILS MODAL */}
      {viewingEvent && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <div>
                <Badge variant="soft" className="normal-case">
                  {viewingEvent.eventType || 'Bonus Event'}
                </Badge>
                <h3 className="mt-1 text-base font-bold text-[var(--gv-ink)]">{viewingEvent.title}</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setViewingEvent(null)}
              />
            </div>

            <div className="space-y-3 text-xs">
              <div className="space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                <span className="block text-[10px] text-[var(--gv-ink-muted)]">Description:</span>
                <p className="font-medium leading-relaxed text-[var(--gv-ink)]">{viewingEvent.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Bonus Points:</span>
                  <strong className="text-sm font-extrabold text-amber-900">+{viewingEvent.pointsValue} Pts</strong>
                </div>

                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Status:</span>
                  <strong className="font-bold text-[var(--gv-ink)]">{viewingEvent.status || 'Active'}</strong>
                </div>

                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Start Date:</span>
                  <strong className="font-mono text-[var(--gv-ink)]">{viewingEvent.startDate || viewingEvent.eventDate || 'N/A'}</strong>
                </div>

                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">End Date:</span>
                  <strong className="font-mono text-[var(--gv-ink)]">{viewingEvent.endDate || viewingEvent.eventDate || 'N/A'}</strong>
                </div>
              </div>

              <div className="space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                <span className="block text-[10px] text-[var(--gv-ink-muted)]">Applicable Levels:</span>
                <strong className="block text-[var(--gv-ink)]">{viewingEvent.eligibleLevels?.join(', ') || 'All Levels'}</strong>
              </div>

              {viewingEvent.applicableStudents && (
                <div className="space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Applicable Students:</span>
                  <strong className="block text-[var(--gv-ink)]">{viewingEvent.applicableStudents}</strong>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-[var(--gv-border)] pt-3">
              <Button variant="secondary" onClick={() => setViewingEvent(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AWARD POINTS MODAL */}
      {isAwardModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-md space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Award Bonus Points to Student</h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsAwardModalOpen(false)}
              />
            </div>

            <form onSubmit={handleAwardSubmit} className="space-y-3.5 text-xs">
              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Select Student</label>
                <select
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.firstName} {s.surname} ({s.id}) - Balance: {s.pointsBalance} pts
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Associated Event (Optional)</label>
                <select
                  value={awardEventTitle}
                  onChange={(e) => {
                    setAwardEventTitle(e.target.value);
                    const matchedEvt = activeEventsList.find((ev) => ev.title === e.target.value);
                    if (matchedEvt) {
                      setAwardPoints(matchedEvt.pointsValue);
                      setAwardReason(`Bonus awarded for ${matchedEvt.title}`);
                    }
                  }}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="">Custom Adjustment (No Specific Event)</option>
                  {activeEventsList.map((ev) => (
                    <option key={ev.id} value={ev.title}>
                      {ev.title} (+{ev.pointsValue} pts)
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Type</label>
                  <select
                    value={awardType}
                    onChange={(e) => setAwardType(e.target.value as 'Credit' | 'Debit')}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Credit">Credit (+ Bonus)</option>
                    <option value="Debit">Debit (- Deduction)</option>
                  </select>
                </div>

                <Input
                  label="Bonus Points"
                  type="number"
                  required
                  className="font-mono font-bold"
                  value={awardPoints}
                  onChange={(e) => setAwardPoints(parseInt(e.target.value) || 0)}
                />
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Reason / Particulars</label>
                <textarea
                  rows={2}
                  required
                  value={awardReason}
                  onChange={(e) => setAwardReason(e.target.value)}
                  placeholder="e.g. Winner in Paryushan Essay Contest"
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-[var(--gv-border)] pt-3">
                <Button type="button" variant="secondary" onClick={() => setIsAwardModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-800 hover:bg-amber-900">
                  Confirm Award
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
